import { useEffect, useRef, useState } from 'react'

import { useNavigate } from '@tanstack/react-router'

import { useSidebar } from '@/components/sui/sidebar'

import { useAccessState } from '../../../access/hooks/use-access-state'
import {
  trackFreePlanBannerClicked,
  trackFreePlanBannerViewed,
} from '../../free-plan-banner.tracking'
import { FreePlanBanner } from '../free-plan-banner'

export function FreePlanBannerWrapper() {
  const navigate = useNavigate()
  const { state, isMobile, setOpenMobile } = useSidebar()
  const hasTrackedView = useRef(false)
  const [bannerVisible, setBannerVisible] = useState(false)

  const { accessState, freePlan } = useAccessState()

  const shouldRender = accessState === 'free' || accessState === 'free-depleted'
  const depleted = accessState === 'free-depleted'

  const envelopesTotal = freePlan?.envelopesTotal ?? 3
  const envelopesLeft = freePlan?.envelopesRemaining ?? 0

  useEffect(() => {
    if (!shouldRender || hasTrackedView.current) return
    hasTrackedView.current = true
    trackFreePlanBannerViewed({
      source: 'sidebar',
      envelopesLeft,
      envelopesTotal,
      depleted,
    })
  }, [shouldRender, envelopesLeft, envelopesTotal, depleted])

  useEffect(() => {
    if (!shouldRender || (!isMobile && state !== 'expanded')) {
      setBannerVisible(false)
      return
    }
    const timer = setTimeout(() => setBannerVisible(true), 300)
    return () => clearTimeout(timer)
  }, [shouldRender, state, isMobile])

  if (!shouldRender) return null

  const handleClick = () => {
    trackFreePlanBannerClicked({
      source: 'sidebar',
      envelopesLeft,
      envelopesTotal,
      depleted,
    })
    // The sheet does not close on navigation, so the banner that navigates
    // has to close it — the same thing every entry in `nav-main` does.
    setOpenMobile(false)
    void navigate({ to: '/settings/billing/plans' })
  }

  const bannerProps = {
    depleted,
    envelopesLeft,
    envelopesTotal,
    onCtaClick: handleClick,
  }

  return (
    <>
      {/* Expanded version */}
      <div className="px-1 group-data-[collapsible=icon]:hidden">
        <div
          className={`transition-all duration-300 ${
            bannerVisible
              ? 'translate-y-0 opacity-100'
              : 'translate-y-2 opacity-0'
          }`}
        >
          <FreePlanBanner {...bannerProps} />
        </div>
      </div>
      {/* Collapsed version (icon only) */}
      <div className="hidden group-data-[collapsible=icon]:block">
        <FreePlanBanner {...bannerProps} collapsed />
      </div>
    </>
  )
}
